import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import {
    PlusCircle, Edit, Trash2, Loader2, AlertCircle, Info, ShieldAlert,
    Clock, Coffee, CheckCircle2, X, Search, Sparkles, CalendarOff, Activity
} from 'lucide-react';

const normalizeBreakType = (val) => {
    if (val === 0 || val === '0') return 'FixedTime';
    if (val === 1 || val === '1') return 'FlexibleAnytime';
    if (val === 2 || val === '2') return 'FlexibleWindow';
    return val || 'FixedTime';
};

const normalizeDeductionPolicy = (val) => {
    if (val === 0 || val === '0') return 'DeductAlways';
    if (val === 1 || val === '1') return 'DeductActualUsage';
    return val || 'DeductAlways';
};

const normalizeUnusedBreakPolicy = (val) => {
    if (val === 0 || val === '0') return 'ForceDeduct';
    if (val === 1 || val === '1') return 'PayToEmployee';
    return val || 'ForceDeduct';
};

// ====================================================================
//     Mola Formu Bileşeni (Geniş ve İnteraktif Modal)
// ====================================================================
const BreakForm = ({ currentBreak, onSave, onCancel, isSubmitting, apiError }) => {
    const buildInitialState = (b) => b ? {
        ...b,
        type: normalizeBreakType(b.type),
        deductionPolicy: normalizeDeductionPolicy(b.deductionPolicy),
        unusedBreakPolicy: normalizeUnusedBreakPolicy(b.unusedBreakPolicy),
        windowStartTime: b.windowStartTime ? b.windowStartTime.substring(0, 5) : '12:00',
        windowEndTime: b.windowEndTime ? b.windowEndTime.substring(0, 5) : '13:00',
    } : {
        name: '',
        durationMinutes: 60,
        isPaid: false,
        type: 'FixedTime',
        windowStartTime: '12:00',
        windowEndTime: '13:00',
        deductionPolicy: 'DeductAlways',
        unusedBreakPolicy: 'ForceDeduct',
        convertUnusedToOvertime: false,
        ignoreOnHolidays: false
    };

    const [formData, setFormData] = useState(() => buildInitialState(currentBreak));

    useEffect(() => {
        setFormData(buildInitialState(currentBreak));
    }, [currentBreak]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            let parsedValue = type === 'checkbox' ? checked : value;
            if (name === 'durationMinutes') {
                parsedValue = value === '' ? '' : parseInt(value, 10);
            }
            const next = { ...prev, [name]: parsedValue };
            if (name === 'isPaid' && checked) {
                next.convertUnusedToOvertime = false;
            }
            return next;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const payload = {
            ...formData,
            durationMinutes: parseInt(formData.durationMinutes, 10) || 1,
            type: normalizeBreakType(formData.type),
            deductionPolicy: normalizeDeductionPolicy(formData.deductionPolicy),
            unusedBreakPolicy: formData.isPaid ? 'ForceDeduct' : normalizeUnusedBreakPolicy(formData.unusedBreakPolicy),
            convertUnusedToOvertime: (!formData.isPaid && formData.deductionPolicy === 'DeductActualUsage' && formData.unusedBreakPolicy === 'PayToEmployee')
                ? !!formData.convertUnusedToOvertime
                : false,
        };

        const formatTime = (t) => {
            if (!t) return null;
            return t.length === 5 ? `${t}:00` : t;
        };

        if (payload.type === 'FlexibleAnytime') {
            payload.windowStartTime = null;
            payload.windowEndTime = null;
        } else {
            payload.windowStartTime = formatTime(payload.windowStartTime);
            payload.windowEndTime = formatTime(payload.windowEndTime);
        }

        onSave(payload);
    };

    // Canlı Hesaplama Motoru Önizleme Metni
    const getRulePreviewText = () => {
        const dur = formData.durationMinutes || 60;
        if (formData.isPaid) {
            if (formData.deductionPolicy === 'DeductActualUsage') {
                return `Bu mola ÜCRETLİDİR. Normal şartlarda personelin çalışma süresinden düşülmez. Ancak turnike takibi açık olduğu için personel molada ${dur} dakikadan fazla dışarıda kalırsa (Örn: ${dur + 15} dk), sadece aşan 15 dakika çalışma süresinden kesilir ve Mola Aşımı olarak raporlanır.`;
            }
            return `Bu mola ÜCRETLİDİR. İşveren tarafından karşılanır ve personelin çalışma süresinden hiçbir kesinti yapılmaz.`;
        }
        if (formData.deductionPolicy === 'DeductAlways') {
            return `Bu mola ÜCRETSİZDİR ve Sabit Kesinti uygulanır. Personel mola saatinde turnikeden kart bassa da basmasa da, vardiya ile kesişen ${dur} dakika çalışma süresinden otomatik olarak düşülür.`;
        }
        if (formData.unusedBreakPolicy === 'ForceDeduct') {
            return `Bu mola ÜCRETSİZDİR ve Turnike Takibi yapılır. Personel molada ${dur} dakikadan az dışarı çıksa bile (Örn: 20 dk) minimum ${dur} dakika düşülür; ${dur} dakikayı aşarsa (Örn: ${dur + 20} dk) fiili çıktığı süre düşülür ve 20 dk Mola Aşımı yazılır.`;
        }
        if (formData.convertUnusedToOvertime) {
            return `Bu mola ÜCRETSİZDİR, Turnike Takibi yapılır ve Kullanılmayan Mola Fazla Mesaiye Dönüşür. Örneğin personel ${dur} dk'lık molada sadece 20 dk dışarı çıkıp 40 dk içeride çalışırsa, bu 40 dakika hem fiili süresine hem de vardiya sonu Fazla Mesai (FM) hesabına bonus olarak eklenir!`;
        }
        return `Bu mola ÜCRETSİZDİR ve Turnike Takibi yapılır (Personele Kazandır). Örneğin personel ${dur} dk'lık molada sadece 20 dk dışarı çıkarsa yalnızca 20 dk kesilir; içeride çalıştığı ${dur - 20 > 0 ? dur - 20 : 0} dakika normal çalışma süresine eklenir (varsa geç giriş/erken çıkışını telafi eder).`;
    };

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl border border-gray-200 dark:border-gray-700 max-h-[92vh] overflow-y-auto">
                {/* Modal Başlığı */}
                <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400">
                            <Coffee size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {formData.id ? 'Mola Kuralını Düzenle' : 'Yeni Mola Kuralı Tanımla'}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Tanımlanan molalar birden fazla vardiyaya bağlanabilir.
                            </p>
                        </div>
                    </div>
                    <button onClick={onCancel} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {apiError && (
                        <div className="text-red-700 bg-red-50 dark:bg-red-900/30 dark:text-red-300 p-3.5 rounded-xl border border-red-200 dark:border-red-800 flex items-center text-sm">
                            <AlertCircle size={18} className="mr-2 flex-shrink-0" />
                            {apiError}
                        </div>
                    )}

                    {/* 1. Mola Adı ve Süresi */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                Mola Adı
                            </label>
                            <input
                                type="text"
                                name="name"
                                id="name"
                                value={formData.name}
                                onChange={handleChange}
                                required
                                placeholder="Örn: Öğle Yemeği Molası, Çay Molası"
                                className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label htmlFor="durationMinutes" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                Mola Süresi (Dakika)
                            </label>
                            <div className="relative">
                                <input
                                    type="number"
                                    name="durationMinutes"
                                    id="durationMinutes"
                                    value={formData.durationMinutes}
                                    onChange={handleChange}
                                    required
                                    min="1"
                                    className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-semibold focus:ring-2 focus:ring-indigo-500"
                                />
                                <span className="absolute right-3 top-2.5 text-xs font-medium text-gray-400">
                                    dk ({((formData.durationMinutes || 0) / 60).toFixed(1)} sa)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* 2. Ücret Durumu Seçim Kartları */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                            Mola Ücret ve Hakediş Durumu
                        </label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, isPaid: false }))}
                                className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 ${
                                    !formData.isPaid
                                        ? 'border-amber-500 bg-amber-50/70 dark:bg-amber-900/20 ring-2 ring-amber-500/20'
                                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                }`}
                            >
                                <div className={`p-2 rounded-lg mt-0.5 ${!formData.isPaid ? 'bg-amber-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                                    <Clock size={18} />
                                </div>
                                <div>
                                    <div className="font-bold text-sm text-gray-900 dark:text-white">Ücretsiz Mola (Süreden Düşülür)</div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        Yasal ara dinlenmesi (Örn: Öğle yemeği). Zorunlu net çalışma süresinden düşülür.
                                    </p>
                                </div>
                            </button>

                            <button
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, isPaid: true, convertUnusedToOvertime: false }))}
                                className={`p-3.5 rounded-xl border text-left transition-all flex items-start gap-3 ${
                                    formData.isPaid
                                        ? 'border-emerald-500 bg-emerald-50/70 dark:bg-emerald-900/20 ring-2 ring-emerald-500/20'
                                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                }`}
                            >
                                <div className={`p-2 rounded-lg mt-0.5 ${formData.isPaid ? 'bg-emerald-600 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-500'}`}>
                                    <CheckCircle2 size={18} />
                                </div>
                                <div>
                                    <div className="font-bold text-sm text-gray-900 dark:text-white">Ücretli Mola (İşveren Karşılar)</div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        Çalışma süresinden düşülmez. Sadece turnike takibi açıksa süre aşımı kesilir.
                                    </p>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* 3. Mola Tipi ve Saat Penceresi */}
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 space-y-4">
                        <div>
                            <label htmlFor="type" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                Mola Zamanlama Tipi
                            </label>
                            <select
                                name="type"
                                id="type"
                                value={formData.type}
                                onChange={handleChange}
                                className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="FixedTime">Sabit Saatli Mola (Örn: 12:00 - 13:00 arası sabit)</option>
                                <option value="FlexibleWindow">Pencereli Esnek Mola (Örn: 11:30 - 14:30 arasında 60 dk)</option>
                                <option value="FlexibleAnytime">Serbest Zamanlı Mola (Vardiya içinde herhangi bir saatte)</option>
                            </select>
                        </div>

                        {(formData.type === 'FixedTime' || formData.type === 'FlexibleWindow') && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                                <div>
                                    <label htmlFor="windowStartTime" className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1">
                                        {formData.type === 'FixedTime' ? 'Mola Başlangıç Saati' : 'Mola Penceresi Başlangıcı'}
                                    </label>
                                    <input
                                        type="time"
                                        name="windowStartTime"
                                        id="windowStartTime"
                                        value={formData.windowStartTime || ''}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                                <div>
                                    <label htmlFor="windowEndTime" className="block text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 mb-1">
                                        {formData.type === 'FixedTime' ? 'Mola Bitiş Saati' : 'Mola Penceresi Bitişi'}
                                    </label>
                                    <input
                                        type="time"
                                        name="windowEndTime"
                                        id="windowEndTime"
                                        value={formData.windowEndTime || ''}
                                        onChange={handleChange}
                                        required
                                        className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 4. Kesinti ve Turnike Takip Politikası */}
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="deductionPolicy" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                                {formData.isPaid ? 'Mola Aşımı Takip Politikası' : 'Mola Kesinti Politikası'}
                            </label>
                            <select
                                name="deductionPolicy"
                                id="deductionPolicy"
                                value={formData.deductionPolicy}
                                onChange={handleChange}
                                className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            >
                                <option value="DeductAlways">
                                    {formData.isPaid ? 'Turnike Takibi Yapma (Standart Ücretli Mola)' : 'Her Zaman Sabit Düş (Turnike Geçişine Bakmaksızın)'}
                                </option>
                                <option value="DeductActualUsage">
                                    {formData.isPaid ? 'Turnike Geçişine Göre Takip Et (Süre Aşımını Kes)' : 'Turnike Geçişine Göre Düş (Fiili Kullanım Takibi)'}
                                </option>
                            </select>
                        </div>

                        {!formData.isPaid && formData.deductionPolicy === 'DeductActualUsage' && (
                            <div className="p-4 rounded-xl bg-indigo-50/70 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 space-y-3">
                                <div>
                                    <label htmlFor="unusedBreakPolicy" className="block text-sm font-semibold text-indigo-950 dark:text-indigo-200 mb-1">
                                        Personel Molayı Az Kullanırsa veya Hiç Çıkmazsa Ne Olsun?
                                    </label>
                                    <select
                                        name="unusedBreakPolicy"
                                        id="unusedBreakPolicy"
                                        value={formData.unusedBreakPolicy}
                                        onChange={handleChange}
                                        className="w-full px-3.5 py-2 border border-indigo-300 dark:border-indigo-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                    >
                                        <option value="ForceDeduct">Yine de Tam Mola Süresini Kes (Zorunlu Kesinti)</option>
                                        <option value="PayToEmployee">Kullanılmayan Süreyi Personele Çalışma Olarak Kazandır</option>
                                    </select>
                                </div>

                                {formData.unusedBreakPolicy === 'PayToEmployee' && (
                                    <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
                                        <input
                                            id="convertUnusedToOvertime"
                                            name="convertUnusedToOvertime"
                                            type="checkbox"
                                            checked={formData.convertUnusedToOvertime}
                                            onChange={handleChange}
                                            className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                                        />
                                        <span className="text-xs text-indigo-900 dark:text-indigo-200 leading-relaxed">
                                            <strong>Kullanılmayan Mola Süresini Fazla Mesaiye (FM) Dönüştür:</strong> İşaretlenirse, personelin molada dışarı çıkmayıp içeride çalıştığı süre vardiya sonunda Fazla Mesai hesabına bonus olarak eklenir.
                                        </span>
                                    </label>
                                )}
                            </div>
                        )}

                        <label className="flex items-start gap-3 p-3.5 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40 cursor-pointer">
                            <input
                                id="ignoreOnHolidays"
                                name="ignoreOnHolidays"
                                type="checkbox"
                                checked={formData.ignoreOnHolidays}
                                onChange={handleChange}
                                className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                            />
                            <div className="text-sm">
                                <div className="font-semibold text-gray-800 dark:text-gray-200">Hafta Tatili ve Resmi Tatillerde Bu Molayı Kesme</div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Personel Hafta Tatili (HT) veya Resmi Tatil (RT) gününde fazla mesaiye geldiğinde bu mola kesintisi uygulanmaz.
                                </p>
                            </div>
                        </label>
                    </div>

                    {/* 5. Canlı Hesaplama Motoru Önizleme Kutusu */}
                    <div className="p-4 rounded-xl bg-gradient-to-r from-slate-900 to-indigo-950 text-white border border-indigo-500/30">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-indigo-300 mb-1.5">
                            <Sparkles size={14} />
                            Hesaplama Motoru Kural Önizlemesi
                        </div>
                        <p className="text-xs text-indigo-100 leading-relaxed">
                            {getRulePreviewText()}
                        </p>
                    </div>

                    {/* Butonlar */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={isSubmitting}
                            className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 font-medium text-sm"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center shadow-lg shadow-indigo-500/25"
                        >
                            {isSubmitting && <Loader2 className="animate-spin mr-2" size={18} />}
                            {formData.id ? 'Değişiklikleri Kaydet' : 'Mola Kuralını Oluştur'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ====================================================================
//     Ana Mola Yönetim Bileşeni
// ====================================================================
export default function BreakManagement({ onDataChange }) {
    const { isAuthenticated, permissions } = useAuth();
    const [breaks, setBreaks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentBreak, setCurrentBreak] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [deleteConfirmData, setDeleteConfirmData] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterPaid, setFilterPaid] = useState('ALL'); // ALL, UNPAID, PAID

    const canView = useMemo(() => permissions.includes('breaks:view_list'), [permissions]);
    const canCreate = useMemo(() => permissions.includes('breaks:create'), [permissions]);
    const canUpdate = useMemo(() => permissions.includes('breaks:update'), [permissions]);
    const canDelete = useMemo(() => permissions.includes('breaks:delete'), [permissions]);

    const fetchBreaks = useCallback(async () => {
        if (!canView) {
            setError("Mola tanımlarını görüntüleme yetkiniz yok.");
            setLoading(false);
            return;
        }
        if (!isAuthenticated) return;
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient('scheduling/breaks', { method: 'GET' });
            setBreaks(data || []);
            if (onDataChange) onDataChange();
        } catch (err) {
            setError(err.message || 'Molalar yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, canView, onDataChange]);

    useEffect(() => {
        fetchBreaks();
    }, [fetchBreaks]);

    const handleOpenModal = (breakItem = null) => {
        const requiredPermission = breakItem ? 'breaks:update' : 'breaks:create';
        if (!permissions.includes(requiredPermission)) {
            alert("Bu işlemi yapma yetkiniz bulunmamaktadır.");
            return;
        }
        setCurrentBreak(breakItem);
        setError(null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        if (!isSubmitting) {
            setIsModalOpen(false);
            setCurrentBreak(null);
            setError(null);
        }
    };

    const handleSave = async (formData) => {
        setIsSubmitting(true);
        setError(null);
        try {
            const method = formData.id ? 'PUT' : 'POST';
            const url = formData.id ? `scheduling/breaks/${formData.id}` : 'scheduling/breaks';
            await apiClient(url, { method, body: formData });
            handleCloseModal();
            fetchBreaks();
        } catch (err) {
            setError(err.message || 'Mola kaydedilirken bir hata oluştu.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        if (!canDelete) {
            setDeleteConfirmData({
                type: 'block',
                message: "Mola silme yetkiniz bulunmamaktadır."
            });
            return;
        }
        setDeleteConfirmData({
            id,
            type: 'initial',
            message: "Bu mola tanımını silmek istediğinizden emin misiniz?"
        });
    };

    const handleDeleteConfirm = async (id, force = false) => {
        try {
            await apiClient(`scheduling/breaks/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' });
            setDeleteConfirmData(null);
            fetchBreaks();
        } catch (err) {
            if (err.status === 409 && err.payload?.requiresConfirmation) {
                setDeleteConfirmData({
                    id,
                    type: 'conflict',
                    message: err.payload.message,
                    usedIn: err.payload.usedIn || []
                });
            } else {
                setDeleteConfirmData({
                    type: 'block',
                    message: err.message || "Mola silinirken bir hata oluştu."
                });
            }
        }
    };

    const filteredBreaks = useMemo(() => {
        return breaks.filter(b => {
            const matchesSearch = !searchQuery || b.name?.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesPaid = filterPaid === 'ALL'
                || (filterPaid === 'PAID' && b.isPaid)
                || (filterPaid === 'UNPAID' && !b.isPaid);
            return matchesSearch && matchesPaid;
        });
    }, [breaks, searchQuery, filterPaid]);

    const stats = useMemo(() => ({
        total: breaks.length,
        unpaid: breaks.filter(b => !b.isPaid).length,
        paid: breaks.filter(b => b.isPaid).length,
        turnstileTracked: breaks.filter(b => normalizeDeductionPolicy(b.deductionPolicy) === 'DeductActualUsage').length
    }), [breaks]);

    const renderBreakTypeInfo = (b) => {
        const type = normalizeBreakType(b.type);
        const start = b.windowStartTime?.substring(0, 5);
        const end = b.windowEndTime?.substring(0, 5);
        if (type === 'FixedTime') {
            return (
                <div className="flex flex-col">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 dark:text-blue-300">
                        <Clock size={13} /> Sabit Saatli
                    </span>
                    {start && end && <span className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">{start} - {end}</span>}
                </div>
            );
        }
        if (type === 'FlexibleWindow') {
            return (
                <div className="flex flex-col">
                    <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
                        <Activity size={13} /> Pencereli Esnek
                    </span>
                    {start && end && <span className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">Pencere: {start} - {end}</span>}
                </div>
            );
        }
        return (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-purple-700 dark:text-purple-300">
                <Sparkles size={13} /> Serbest Zamanlı (Gün İçi)
            </span>
        );
    };

    const renderBreakPolicyBadges = (b) => {
        const deduction = normalizeDeductionPolicy(b.deductionPolicy);
        const unused = normalizeUnusedBreakPolicy(b.unusedBreakPolicy);

        return (
            <div className="flex flex-wrap gap-1.5">
                {b.isPaid ? (
                    deduction === 'DeductActualUsage' ? (
                        <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
                            Turnike Takibi • Sadece Aşım Kesilir
                        </span>
                    ) : (
                        <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
                            Kesinti Yok (Tam Ücretli)
                        </span>
                    )
                ) : deduction === 'DeductAlways' ? (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700">
                        Her Zaman Sabit Düşülür
                    </span>
                ) : unused === 'PayToEmployee' ? (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:border-indigo-800">
                        Turnike Takibi • Kullanılmayanı Kazandır {b.convertUnusedToOvertime ? '(+FM)' : ''}
                    </span>
                ) : (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-lg bg-amber-50 text-amber-800 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800">
                        Turnike Takibi • Zorunlu Tam Kesinti
                    </span>
                )}

                {b.ignoreOnHolidays && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800">
                        <CalendarOff size={12} /> Tatilde Kesilmez
                    </span>
                )}
            </div>
        );
    };

    if (!loading && !canView) {
        return (
            <div className="p-8 bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-200 dark:border-red-800 text-center">
                <ShieldAlert className="mx-auto h-12 w-12 text-red-500" />
                <h3 className="mt-2 text-lg font-bold text-red-800 dark:text-red-300">Yetki Hatası</h3>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* KPI Özet Kartları */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Toplam Mola</p>
                        <p className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                        <Coffee size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Ücretsiz Molalar</p>
                        <p className="text-2xl font-extrabold text-amber-600 dark:text-amber-400 mt-1">{stats.unpaid}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                        <Clock size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Ücretli Molalar</p>
                        <p className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1">{stats.paid}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                        <CheckCircle2 size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Turnike Takipli</p>
                        <p className="text-2xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-1">{stats.turnstileTracked}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                        <Activity size={22} />
                    </div>
                </div>
            </div>

            {/* Ana Tablo Kartı */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                {/* Üst Araç Çubuğu */}
                <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Coffee size={20} className="text-amber-500" />
                            Mola Tanımları ve Kesinti Kuralları
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Vardiyalara bağlanacak yemek, çay ve ara dinlenme kurallarını yapılandırın.
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Mola adı ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <div className="inline-flex rounded-xl bg-gray-100 dark:bg-gray-700 p-1">
                            {[
                                { id: 'ALL', label: 'Tümü' },
                                { id: 'UNPAID', label: 'Ücretsiz' },
                                { id: 'PAID', label: 'Ücretli' }
                            ].map(f => (
                                <button
                                    key={f.id}
                                    type="button"
                                    onClick={() => setFilterPaid(f.id)}
                                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                                        filterPaid === f.id
                                            ? 'bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                            : 'text-gray-600 dark:text-gray-300 hover:text-gray-900'
                                    }`}
                                >
                                    {f.label}
                                </button>
                            ))}
                        </div>

                        {canCreate && (
                            <button
                                onClick={() => handleOpenModal()}
                                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl flex items-center shadow-sm shadow-indigo-500/20 transition-colors"
                            >
                                <PlusCircle size={17} className="mr-1.5" />
                                Yeni Mola Ekle
                            </button>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="p-12 flex justify-center items-center text-gray-500">
                        <Loader2 className="animate-spin mr-2" size={20} /> Molalar yükleniyor...
                    </div>
                )}

                {error && !isModalOpen && (
                    <div className="m-5 text-red-600 bg-red-50 dark:bg-red-900/20 p-4 rounded-xl flex items-center text-sm">
                        <AlertCircle size={18} className="mr-2" /> {error}
                    </div>
                )}

                {!loading && !error && (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-50/80 dark:bg-gray-900/50">
                                <tr>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Mola Adı</th>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Zamanlama & Pencere</th>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Süre</th>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ücret Durumu</th>
                                    <th className="px-6 py-3.5 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hesaplama & Kesinti Politikası</th>
                                    {(canUpdate || canDelete) && <th className="px-6 py-3.5 text-right text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">İşlemler</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700/70">
                                {filteredBreaks.map(b => (
                                    <tr key={b.id} className="hover:bg-gray-50/80 dark:hover:bg-gray-700/30 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className={`p-2 rounded-lg ${b.isPaid ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-50 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'}`}>
                                                    <Coffee size={17} />
                                                </div>
                                                <span className="text-sm font-bold text-gray-900 dark:text-white">{b.name}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {renderBreakTypeInfo(b)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold text-xs">
                                                {b.durationMinutes} dk
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {b.isPaid ? (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300">
                                                    <CheckCircle2 size={12} /> Ücretli
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300">
                                                    <Clock size={12} /> Ücretsiz (Düşülür)
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            {renderBreakPolicyBadges(b)}
                                        </td>
                                        {(canUpdate || canDelete) && (
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="inline-flex items-center gap-1.5">
                                                    {canUpdate && (
                                                        <button
                                                            onClick={() => handleOpenModal(b)}
                                                            className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30 transition-colors"
                                                            title="Düzenle"
                                                        >
                                                            <Edit size={17} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(b.id)}
                                                            className="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30 transition-colors"
                                                            title="Sil"
                                                        >
                                                            <Trash2 size={17} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredBreaks.length === 0 && (
                            <div className="text-center py-12 text-gray-500 dark:text-gray-400 text-sm">
                                Kriterlere uygun mola tanımı bulunamadı.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {isModalOpen && (
                <BreakForm
                    currentBreak={currentBreak}
                    onSave={handleSave}
                    onCancel={handleCloseModal}
                    isSubmitting={isSubmitting}
                    apiError={error}
                />
            )}

            {/* Dinamik Silme Modalı */}
            {deleteConfirmData && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start mb-4">
                            <div className={`flex-shrink-0 p-3 rounded-full mr-4 ${
                                deleteConfirmData.type === 'block' ? 'bg-red-100 dark:bg-red-900/30' :
                                deleteConfirmData.type === 'conflict' ? 'bg-amber-100 dark:bg-amber-900/30' :
                                'bg-red-100 dark:bg-red-900/30'
                            }`}>
                                {deleteConfirmData.type === 'block' ? (
                                    <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
                                ) : deleteConfirmData.type === 'conflict' ? (
                                    <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                                ) : (
                                    <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                    {deleteConfirmData.type === 'block' ? 'İşlem Engellendi' :
                                     deleteConfirmData.type === 'conflict' ? 'Uyarı: Bağlı Vardiyalar Bulundu' :
                                     'Silme Onayı'}
                                </h3>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {deleteConfirmData.message}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => setDeleteConfirmData(null)}
                                className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium text-sm"
                            >
                                {deleteConfirmData.type === 'block' ? 'Kapat' : 'İptal'}
                            </button>
                            {deleteConfirmData.type !== 'block' && (
                                <button
                                    onClick={() => handleDeleteConfirm(deleteConfirmData.id, deleteConfirmData.type === 'conflict')}
                                    className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-sm flex items-center shadow-lg shadow-red-500/30"
                                >
                                    {deleteConfirmData.type === 'conflict' ? 'Onaylıyorum, Zorla Sil' : 'Evet, Sil'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
